import * as React from 'react';

import { FontOption } from './types';
import { getCachedFonts, setCachedFonts, loadFont } from './font-utils';

export interface UseFontOptionsProps {
  apiKey: string;
  maxFonts?: number;
  onError?: (error: Error) => void;
}

export interface UseFontOptionsReturn {
  fonts: FontOption[];
  isLoading: boolean;
  loadFont: typeof loadFont;
}

/**
 * Hook to fetch and manage Google Fonts options
 * Handles caching, loading state, and font stylesheet loading
 */
export function useFontOptions({ apiKey, maxFonts = 500, onError }: UseFontOptionsProps): UseFontOptionsReturn {
  const [fonts, setFonts] = React.useState<FontOption[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);

  // Stabilize onError callback reference to prevent effect re-runs when
  // consumers pass inline functions (which create new references on every render)
  const onErrorRef = React.useRef(onError);
  React.useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  React.useEffect(() => {
    const fetchFonts = async () => {
      // Check cache first
      const cachedFonts = getCachedFonts();
      if (cachedFonts && cachedFonts.length > 0) {
        setFonts(cachedFonts.slice(0, maxFonts));
        setIsLoading(false);

        return;
      }

      try {
        const response = await fetch(`https://www.googleapis.com/webfonts/v1/webfonts?key=${apiKey}&sort=popularity`);

        if (!response.ok) {
          throw new Error(`Failed to fetch fonts: ${response.statusText}`);
        }

        const data = await response.json();

        const fontOptions: FontOption[] = data.items.map((fontItem: { family: string; category: string; variants: string[] }) => ({
          value: fontItem.family,
          label: fontItem.family,
          category: fontItem.category,
          variants: fontItem.variants,
        }));

        // Cache all fonts, but only display up to maxFonts
        setCachedFonts(fontOptions);
        setFonts(fontOptions.slice(0, maxFonts));
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to fetch fonts');

        console.error('FontPicker: Failed to fetch Google Fonts', error);
        onErrorRef.current?.(error);
      } finally {
        setIsLoading(false);
      }
    };

    void fetchFonts();
  }, [apiKey, maxFonts]);

  return { fonts, isLoading, loadFont };
}
